// Préférences locales du joueur (confort par appareil). Stockage navigateur optionnel :
// tout fonctionne si localStorage est indisponible (navigation privée, aperçu).
export interface Settings {
  volume: number;
  /** Volumes par catégorie (multipliés par le volume général). */
  sfxVolume: number;
  uiVolume: number;
  /** Couper le son quand l'onglet n'est pas visible. */
  muteHidden: boolean;
  /** Couleurs des camps, équipes et zones adaptées aux daltonismes. */
  colorMode: 'standard' | 'redgreen' | 'blueyellow';
  /** Taille des textes de l'interface (1 = normale). */
  uiScale: number;
  highContrast: boolean;
  /** Repères visuels des sons (bord de l'écran quand on est touché). */
  visualCues: boolean;
  particles: 'low' | 'medium' | 'high';
  shake: boolean;
  reducedMotion: boolean;
  autofire: boolean;
  joyOpacity: number;
  joySensitivity: number;
  deadzone: number;
  joyFixed: boolean;
  quality: number;
  /** Langue choisie (code de LANGS dans i18n), null = langue du navigateur. */
  lang: string | null;
  name: string;
}

const KEY = 'tankshift.settings.v1';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export const DEFAULT_SETTINGS: Settings = {
  volume: 0.6,
  sfxVolume: 1,
  uiVolume: 1,
  muteHidden: true,
  colorMode: 'standard',
  uiScale: 1,
  highContrast: false,
  visualCues: true,
  particles: 'medium',
  shake: false,
  reducedMotion: false,
  autofire: false,
  joyOpacity: 0.55,
  joySensitivity: 1,
  deadzone: 0.18,
  joyFixed: false,
  quality: 1,
  lang: null,
  name: '',
};

let settings: Settings = load();
const listeners = new Set<(s: Settings) => void>();

function load(): Settings {
  const base = { ...DEFAULT_SETTINGS, reducedMotion: prefersReducedMotion() };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return base;
    return { ...base, ...JSON.parse(raw) };
  } catch {
    return base;
  }
}

export function getSettings(): Settings {
  return settings;
}

export function updateSettings(patch: Partial<Settings>): void {
  settings = { ...settings, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    /* stockage indisponible : réglage gardé pour la session */
  }
  for (const fn of listeners) fn(settings);
}

export function onSettings(fn: (s: Settings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
